import { createEffect, createSignal, For, Show, untrack } from "solid-js";
import Text from "../../components/Text";
import Modal from "../../components/Modal";
import ItemIdentifierPicker from "../../components/ItemIdentifierPicker";
import { useTranslation } from "../../hooks/useTranslation";
import { formatDateTime } from "../../services/dateFormat";
import { useCraftingResolver } from "../../services/craftingResolverContext";
import type { ChestInfo, FilterItem } from "../../services/inventoryApi";
import shared from "./storageShared.module.css";

export const UNCHANGED = -1;

const CHEST_TYPES = [
  { value: 0, label: "STORAGE_CHEST_TYPE_SINGLE" },
  { value: 1, label: "STORAGE_CHEST_TYPE_DOUBLE" },
  { value: 2, label: "STORAGE_CHEST_TYPE_INFINITE" },
];

const FILTER_MODES = [
  { value: 0, label: "STORAGE_FILTER_DISABLED" },
  { value: 1, label: "STORAGE_FILTER_WHITELIST" },
  { value: 2, label: "STORAGE_FILTER_BLACKLIST" },
];

export interface ChestConfigValue {
  type: number;
  filterMode: number;
  filterItems: FilterItem[];
}

interface Props {
  open: boolean;
  chest: ChestInfo | null;
  bulkCount: number | null;
  saving: boolean;
  onClose: () => void;
  onSave: (value: ChestConfigValue) => void;
}

export default function ChestConfigModal(props: Props) {
  const { t } = useTranslation();
  const resolver = useCraftingResolver();

  const [type, setType] = createSignal(0);
  const [filterMode, setFilterMode] = createSignal(0);
  const [filterItems, setFilterItems] = createSignal<FilterItem[]>([]);
  const [error, setError] = createSignal<string | null>(null);

  const isBulk = () => props.bulkCount != null;

  createEffect(() => {
    if (!props.open) return;
    untrack(() => {
      setError(null);
      if (props.chest) {
        setType(props.chest.type ?? 0);
        setFilterMode(props.chest.filterMode ?? 0);
        setFilterItems(props.chest.filterItems ? [...props.chest.filterItems] : []);
      } else {
        setType(UNCHANGED);
        setFilterMode(UNCHANGED);
        setFilterItems([]);
      }
    });
  });

  // A double chest spans two blocks and only a scan knows where the partner sits, so bulk cannot
  // promote chests to it. The backend rejects it too.
  const typeOptions = () => isBulk() ? CHEST_TYPES.filter(ct => ct.value !== 1) : CHEST_TYPES;

  function itemsToIdentifiers(items: FilterItem[]): string[] {
    const r = resolver();
    return items.map(item => {
      const def = r?.getItemById(item.itemId, item.metadata) ?? r?.getItemById(item.itemId);
      return def ? `minecraft:${def.name}` : `minecraft:${item.itemId}`;
    });
  }

  function identifiersToItems(identifiers: string[]): FilterItem[] {
    const all = resolver()?.getAllItems() ?? [];
    return identifiers
      .map(identifier => {
        const bare = identifier.replace(/^minecraft:/, "");
        const def = all.find(d => d.name === bare);
        return def ? { itemId: def.id, metadata: def.metadata ?? 0 } : null;
      })
      .filter((x): x is FilterItem => x !== null);
  }

  function submit() {
    if (type() === 2) {
      if (filterMode() !== 1) { setError(t("STORAGE_FILTER_INFINITE_WHITELIST")); return; }
    }
    if (isBulk() && type() === UNCHANGED && filterMode() === UNCHANGED) {
      setError(t("STORAGE_BULK_NOTHING_TO_APPLY"));
      return;
    }
    setError(null);
    props.onSave({ type: type(), filterMode: filterMode(), filterItems: filterItems() });
  }

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title={isBulk()
        ? <span>{t("STORAGE_CONFIGURE_N_CHESTS", props.bulkCount ?? 0)}</span>
        : <Text val="STORAGE_CONFIGURE_CHEST" />}
      leading={<i class="fa-solid fa-gear" />}
    >
      <div class="v" style={{ gap: "var(--spacing-md)" }}>
        <label class="v">
          <span class={shared.fieldLabel}><Text val="STORAGE_TYPE" /></span>
          <select value={type()} onChange={e => setType(parseInt(e.currentTarget.value))}>
            <Show when={isBulk()}>
              <option value={UNCHANGED}>{t("STORAGE_BULK_UNCHANGED")}</option>
            </Show>
            <For each={typeOptions()}>{(ct) => <option value={ct.value}>{t(ct.label)}</option>}</For>
          </select>
        </label>

        <label class="v">
          <span class={shared.fieldLabel}><Text val="STORAGE_FILTER" /></span>
          <select value={filterMode()} onChange={e => setFilterMode(parseInt(e.currentTarget.value))}>
            <Show when={isBulk()}>
              <option value={UNCHANGED}>{t("STORAGE_BULK_UNCHANGED")}</option>
            </Show>
            <For each={FILTER_MODES}>{(fm) => <option value={fm.value}>{t(fm.label)}</option>}</For>
          </select>
        </label>

        <Show when={filterMode() > 0}>
          <label class="v">
            <span class={shared.fieldLabel}>
              <Text val="STORAGE_FILTER_ITEMS" />
              <Show when={type() === 2}> <span class={shared.fieldHint}>({t("STORAGE_FILTER_INFINITE_HINT")})</span></Show>
            </span>
            <ItemIdentifierPicker
              value={itemsToIdentifiers(filterItems())}
              onChange={(next) => {
                const limited = type() === 2 && next.length > 1 ? next.slice(-1) : next;
                setFilterItems(identifiersToItems(limited));
              }}
              placeholder={t("STORAGE_FILTER_SEARCH_ITEM")}
            />
            <Show when={type() === 2 && filterItems().length === 0}>
              <span class={shared.fieldHint}><Text val="STORAGE_FILTER_INFINITE_EMPTY_HELP" /></span>
            </Show>
          </label>
        </Show>

        <Show when={type() === 2 && !isBulk() && props.chest}>
          {(() => {
            const chest = props.chest!;
            const checkedAt = chest.suctionCheckedAt;
            return (
              <div class="v">
                <span class={shared.fieldLabel}><Text val="STORAGE_SUCTION" /></span>
                <div class={shared.stateRow}>
                  <span class={`${shared.stateBadge} ${!checkedAt
                    ? shared.stateUnknown
                    : chest.suctionEnabled ? shared.stateOn : shared.stateOff}`}>
                    <Text val={!checkedAt
                      ? "STORAGE_SUCTION_STATE_UNCHECKED"
                      : chest.suctionEnabled ? "STORAGE_SUCTION_STATE_ON" : "STORAGE_SUCTION_STATE_OFF"} />
                  </span>
                  <span class={shared.fieldHint}>
                    {checkedAt
                      ? t("STORAGE_SUCTION_CHECKED_AT", formatDateTime(checkedAt))
                      : t("STORAGE_SUCTION_UNCHECKED_HINT")}
                  </span>
                </div>
              </div>
            );
          })()}
        </Show>

        <Show when={error()}><div class={shared.inlineError}>{error()}</div></Show>

        <div class="h" style={{ "justify-content": "flex-end", gap: "var(--spacing-sm)" }}>
          <button class="btn btn-secondary" onClick={props.onClose}><Text val="CANCEL" /></button>
          <button class="btn btn-primary" onClick={submit} disabled={props.saving}>
            <Show when={props.saving} fallback={<Text val="SAVE" />}><i class="fa-solid fa-spinner fa-spin" /></Show>
          </button>
        </div>
      </div>
    </Modal>
  );
}
